import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Shell } from "@/components/Shell";
import { CheckCircle2, MessageSquare } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SmsOptIn() {
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [consent, setConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    // Tri-state success: null = no submission yet, "opted_in" = full opt-in
    // success, "not_opted" = submitted without phone and/or consent (the
    // Twilio-compliance path — surfaces a friendly notice + resubmit link).
    const [result, setResult] = useState(null);

    const formatPhone = (raw) => {
        const d = raw.replace(/\D/g, "").slice(0, 10);
        if (d.length === 0) return "";
        if (d.length < 4) return `(${d}`;
        if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const digits = phone.replace(/\D/g, "");
        // Soft-validate phone only when the user provided one. Empty phone is
        // a valid "no opt-in" submission per Twilio campaign-review guidance.
        if (digits.length > 0 && digits.length !== 10) {
            setError("Please enter a valid 10-digit US mobile number, or leave the field blank.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${BACKEND_URL}/api/sms/opt-in`, {
                phone: digits.length === 10 ? `+1${digits}` : null,
                consent: !!consent,
                full_name: name || null,
                source_url: window.location.href,
            });
            setResult(res.data?.opted_in ? "opted_in" : "not_opted");
        } catch (err) {
            setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setResult(null);
        setError("");
    };

    return (
        <Shell>
            <section className="section-pad">
                <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-start">
                    {/* Left: requirements / context */}
                    <div data-testid="sms-optin-info">
                        <div className="label-base">Stay in the loop</div>
                        <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">Text alerts for your health fair.</h1>
                        <p className="mt-5 text-lg text-brand-mute">Get a short text when an event you applied to has news — approvals, payment reminders, day-of logistics. No marketing. No spam.</p>

                        <ul className="mt-8 space-y-4 text-sm text-brand-ink">
                            <li className="flex gap-3"><CheckCircle2 className="text-brand-forest mt-0.5 shrink-0" size={18} /><span><strong>What you'll receive:</strong> event reminders and vendor application status updates from HealthFairX.</span></li>
                            <li className="flex gap-3"><CheckCircle2 className="text-brand-forest mt-0.5 shrink-0" size={18} /><span><strong>Message frequency:</strong> event-driven — typically a few messages per month, more in the week of your event.</span></li>
                            <li className="flex gap-3"><CheckCircle2 className="text-brand-forest mt-0.5 shrink-0" size={18} /><span><strong>Message &amp; data rates may apply</strong> depending on your mobile carrier and plan.</span></li>
                            <li className="flex gap-3"><CheckCircle2 className="text-brand-forest mt-0.5 shrink-0" size={18} /><span><strong>Reply HELP</strong> for help, <strong>reply STOP</strong> to unsubscribe at any time.</span></li>
                            <li className="flex gap-3"><CheckCircle2 className="text-brand-forest mt-0.5 shrink-0" size={18} /><span>By submitting, you agree to our <Link to="/terms" className="underline text-brand-forest" data-testid="info-terms-link">Terms of Service</Link> and <Link to="/privacy" className="underline text-brand-forest" data-testid="info-privacy-link">Privacy Policy</Link>.</span></li>
                        </ul>
                    </div>

                    {/* Right: the form */}
                    <div className="rounded-2xl border border-brand-line bg-white p-8 shadow-sm" data-testid="sms-optin-card">
                        {result === "opted_in" ? (
                            <div className="text-center py-8" data-testid="sms-optin-success">
                                <div className="mx-auto h-14 w-14 rounded-full bg-brand-forest/10 inline-flex items-center justify-center mb-4">
                                    <CheckCircle2 className="text-brand-forest" size={28} />
                                </div>
                                <h2 className="font-heading text-2xl font-bold">You're signed up.</h2>
                                <p className="mt-3 text-brand-mute">Check your phone — we just sent a confirmation text. Reply <strong>STOP</strong> any time to unsubscribe.</p>
                            </div>
                        ) : result === "not_opted" ? (
                            <div className="py-6" data-testid="sms-optin-not-opted">
                                <h2 className="font-heading text-2xl font-bold">No SMS opt-in recorded.</h2>
                                <p className="mt-4 text-brand-ink leading-relaxed">
                                    You have decided not to opt into SMS notifications, as you did not provide a phone number or check the box to agree to receive transactional SMS from <strong>HealthFairX</strong>.
                                </p>
                                <p className="mt-4 text-brand-mute text-sm">
                                    If this was done in error, please <button type="button" onClick={resetForm} data-testid="sms-optin-resubmit-link" className="underline font-semibold text-brand-forest hover:text-brand-forest-dark">resubmit the form here</button>.
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="sms-optin-form" noValidate>
                                <div className="flex items-center gap-2 mb-1 text-brand-forest">
                                    <MessageSquare size={18} />
                                    <div className="text-xs font-bold uppercase tracking-widest">HealthFairX Text Alerts</div>
                                </div>
                                <h2 className="font-heading text-2xl font-bold mb-6">Sign up for text alerts</h2>

                                <label htmlFor="full_name" className="block text-sm font-semibold text-brand-ink">Name <span className="text-brand-mute font-normal">(optional)</span></label>
                                <input
                                    id="full_name"
                                    data-testid="sms-optin-name-input"
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Jane Doe"
                                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest"
                                />

                                <label htmlFor="phone" className="block text-sm font-semibold text-brand-ink mt-5">Mobile phone number <span className="text-brand-mute font-normal">(optional)</span></label>
                                <input
                                    id="phone"
                                    data-testid="sms-optin-phone-input"
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel-national"
                                    value={phone}
                                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                                    placeholder="(555) 123-4567"
                                    className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest"
                                />

                                <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
                                    <input
                                        data-testid="sms-optin-consent-checkbox"
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        className="mt-1 h-4 w-4 rounded border-brand-line text-brand-forest focus:ring-brand-forest"
                                    />
                                    <span className="text-sm text-brand-ink leading-relaxed">
                                        Yes, I would like to receive automated text messages from <strong>HealthFairX</strong> about event reminders and vendor application status updates. I understand message frequency varies and is event-driven (typically a few messages per month).
                                    </span>
                                </label>

                                <div className="mt-5 space-y-2 text-xs text-brand-mute leading-relaxed">
                                    <p><strong className="text-brand-ink">Message Frequency:</strong> Event-driven. Most users receive a few messages per month.</p>
                                    <p><strong className="text-brand-ink">Standard Rates:</strong> Message and data rates may apply depending on your mobile phone service plan.</p>
                                    <p><strong className="text-brand-ink">Help &amp; Stop:</strong> Reply HELP for help, or STOP to cancel at any time.</p>
                                    <p>Consent is not required to use the HealthFairX platform. To opt in, provide your phone number AND check the box above. See our <Link to="/terms" className="underline" data-testid="sms-optin-terms-link">Terms of Service</Link> and <Link to="/privacy" className="underline" data-testid="sms-optin-privacy-link">Privacy Policy</Link>.</p>
                                </div>

                                {error && (
                                    <div className="mt-4 rounded-lg border border-brand-coral/40 bg-brand-coral/10 px-3 py-2 text-sm text-brand-coral" data-testid="sms-optin-error">
                                        {error}
                                    </div>
                                )}

                                <button
                                    data-testid="sms-optin-submit-button"
                                    type="submit"
                                    disabled={submitting}
                                    className="mt-6 w-full pill bg-brand-forest text-white px-6 py-3.5 font-semibold hover:bg-brand-forest-dark transition-colors disabled:opacity-60"
                                >
                                    {submitting ? "Submitting…" : "Submit"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>
        </Shell>
    );
}
